<template>
  <div class="searchable-product-select form-group" :class="{ 'form-group--has-error': error }">
    <label v-if="label" :for="id">{{ label }}</label>
    <div ref="triggerRef" class="select-wrapper">
      <input
        :id="id"
        type="text"
        v-model="searchQuery"
        @input="handleSearch"
        @focus="handleFocus"
        @blur="handleBlur"
        :placeholder="placeholder"
        class="select-input"
        autocomplete="off"
      />
      <BaseIcon name="chevron-down" class="select-icon" />
    </div>

    <!-- Dropdown content (rendered into Tippy / appended to body) -->
    <div ref="dropdownRef" class="searchable-product-select__panel">
      <div v-if="searchQuery.trim().length < MIN_CHARS" class="dropdown-empty">
        {{ $t('products.search.min_chars') }}
      </div>
      <div v-else-if="isLoading && !results.length" class="dropdown-loading">
        {{ $t('common.loading') }}
      </div>
      <div v-else-if="hasError" class="dropdown-empty">{{ $t('products.search.error') }}</div>
      <template v-else-if="results.length">
        <div
          v-for="product in results"
          :key="product.id"
          class="dropdown-item"
          @mousedown.prevent="selectProduct(product)"
        >
          <div class="product-name">{{ product.title }}</div>
          <div class="product-meta">
            <span class="product-sku">
              {{ product.base_sku || '-' }}
              <span v-if="product.unit" class="product-unit">
                {{ shortUnitLabel(product.unit, $t) }}
              </span>
            </span>
            <span v-if="product.price != null" class="product-price">
              {{ formatCurrency(product.price) }}
            </span>
          </div>
        </div>
      </template>
      <div v-else class="dropdown-empty">{{ $t('products.search.no_results') }}</div>
    </div>
    <span v-if="error" class="form-group__error">{{ error }}</span>
  </div>
</template>

<script>
  import { ref, onBeforeUnmount, onMounted, nextTick, watch } from 'vue';
  import { debounce } from 'lodash';
  import tippy from 'tippy.js';
  import BaseIcon from '@/components/partials/BaseIcon.vue';
  import { shortUnitLabel } from '@/utils/productUnit';
  import productService from '@shared/services/productService';

  const MIN_CHARS = 2;
  const RESULT_LIMIT = 15;

  export default {
    name: 'SearchableProductSelect',

    components: {
      BaseIcon,
    },

    props: {
      id: {
        type: String,
        required: true,
      },
      label: {
        type: String,
        default: '',
      },
      modelValue: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: 'Zoek product of typ zelf...',
      },
      error: {
        type: String,
        default: '',
      },
    },

    emits: ['productSelected', 'update:modelValue'],

    setup(props, { emit }) {
      const results = ref([]);
      const searchQuery = ref(props.modelValue || '');
      const searchedTerm = ref(null);
      const showDropdown = ref(false);
      const isLoading = ref(false);
      const hasError = ref(false);
      const triggerRef = ref(null);
      const dropdownRef = ref(null);
      let tippyInstance = null;
      let requestId = 0;

      const search = debounce(async (term) => {
        const current = ++requestId;
        isLoading.value = true;
        hasError.value = false;
        try {
          const data = await productService.searchProducts({ query: term, limit: RESULT_LIMIT });
          if (current !== requestId) return;
          results.value = data?.products || [];
          searchedTerm.value = term;
        } catch (error) {
          if (current !== requestId) return;
          console.error('Product search failed:', error);
          results.value = [];
          hasError.value = true;
        } finally {
          if (current === requestId) isLoading.value = false;
        }
      }, 250);

      const runSearch = () => {
        const term = searchQuery.value.trim();
        if (term.length < MIN_CHARS) {
          requestId += 1;
          search.cancel();
          results.value = [];
          searchedTerm.value = null;
          isLoading.value = false;
          return;
        }
        if (term !== searchedTerm.value) search(term);
      };

      const handleSearch = () => {
        showDropdown.value = true;
        emit('update:modelValue', searchQuery.value);
        runSearch();
      };

      const handleFocus = () => {
        showDropdown.value = true;
        runSearch();
      };

      // Handle blur with delay for click events
      const handleBlur = () => {
        setTimeout(() => {
          showDropdown.value = false;
        }, 200);
      };

      // Search results are slim; listeners expect the full product (options, VAT, image).
      const selectProduct = async (product) => {
        searchQuery.value = product.title;
        searchedTerm.value = product.title;
        showDropdown.value = false;
        emit('update:modelValue', searchQuery.value);

        let fullProduct = null;
        try {
          const data = await productService.getProductById(product.id);
          fullProduct = data?.product || data;
        } catch (error) {
          console.error('Failed to load selected product:', error);
        }
        emit(
          'productSelected',
          fullProduct || {
            ...product,
            baseSku: product.base_sku,
            imageUrl: product.image_url,
          }
        );
      };

      // Currency formatter
      const formatCurrency = (amount) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
        }).format(amount);
      };

      // Sync searchQuery with modelValue
      watch(
        () => props.modelValue,
        (newValue) => {
          if (newValue !== searchQuery.value) {
            searchQuery.value = newValue;
          }
        }
      );

      // Tippy owns the panel: appending it to the body means no parent's
      // overflow can clip it, and Popper flips it above the input when there
      // is no room below.
      onMounted(() => {
        nextTick(() => {
          if (!triggerRef.value || !dropdownRef.value) return;

          tippyInstance = tippy(triggerRef.value, {
            content: dropdownRef.value,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            animation: 'shift-away',
            theme: 'dropdown',
            appendTo: () => document.body,
            maxWidth: 'none',
            offset: [0, 4],
            popperOptions: {
              modifiers: [
                { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
              ],
            },
            onShow(instance) {
              const triggerWidth = triggerRef.value?.offsetWidth || 0;
              const maxWidth = Math.max(window.innerWidth - 16, 0);
              const width = Math.min(Math.max(triggerWidth, 360), maxWidth);
              instance.popper.style.width = `${width}px`;
            },
          });
        });
      });

      onBeforeUnmount(() => {
        search.cancel();
        tippyInstance?.destroy();
        tippyInstance = null;
      });

      watch(showDropdown, (open) => {
        if (!tippyInstance) return;
        if (open) tippyInstance.show();
        else tippyInstance.hide();
      });

      // The result list grows and shrinks while typing, so the placement has
      // to be recalculated or the panel keeps the position it opened with.
      watch([results, isLoading, hasError], () => {
        if (!showDropdown.value) return;
        nextTick(() => tippyInstance?.popperInstance?.update());
      });

      return {
        MIN_CHARS,
        searchQuery,
        showDropdown,
        isLoading,
        hasError,
        results,
        triggerRef,
        dropdownRef,
        handleSearch,
        handleFocus,
        handleBlur,
        selectProduct,
        formatCurrency,
        shortUnitLabel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .searchable-product-select {
    position: relative;
    width: 100%;
  }

  .select-wrapper {
    position: relative;
    width: 100%;
  }

  .select-input {
    width: 100%;
    padding: 0.5rem 2.5rem 0.5rem 0.75rem;
    border: 1px solid var(--slate-300);
    border-radius: 0.375rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--slate-900);
    background: white;
    text-overflow: ellipsis;

    &:focus {
      outline: none;
      border-color: var(--brand-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    &::placeholder {
      color: var(--slate-400);
    }
  }

  .select-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--slate-400);
    pointer-events: none;
  }

  // Hidden until Tippy mounts it into the popper; the panel's frame comes from
  // the shared 'dropdown' theme.
  .searchable-product-select__panel {
    display: none;
    max-height: 300px;
    overflow-y: auto;
  }

  .dropdown-loading,
  .dropdown-empty {
    padding: 0.75rem;
    text-align: center;
    color: var(--slate-500);
    font-size: 0.875rem;
  }

  .dropdown-item {
    padding: 0.75rem;
    cursor: pointer;
    border-bottom: 1px solid var(--slate-100);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: var(--slate-50);
    }
  }

  .product-name {
    font-weight: 500;
    color: var(--slate-900);
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    overflow-wrap: break-word;
  }

  .product-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.75rem;
  }

  .product-sku {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--slate-500);
  }

  .product-unit {
    padding: 0 0.375rem;
    border-radius: 999px;
    background: var(--slate-100);
    color: var(--slate-600);
  }

  .product-price {
    color: var(--brand-600);
    font-weight: 600;
  }

  .form-group--has-error {
    .select-input {
      border-color: var(--red-500);
    }
  }

  .form-group__error {
    display: block;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: var(--red-500);
  }
</style>

<style lang="scss">
  .tippy-box[data-theme~='dropdown'] .searchable-product-select__panel {
    display: block;
  }
</style>
